/**
 * Street View Comparison Component - GeoSpy-inspired design
 * Split screen with Street View on left and uploaded image on right
 */

import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Crosshair, AlertCircle } from 'lucide-react';

export default function StreetViewComparison({
  coordinates,
  uploadedImage,
  onBack,
  isVisible
}) {
  const streetViewRef = useRef(null);
  const panorama = useRef(null);
  const [streetViewError, setStreetViewError] = useState(null);
  const [loading, setLoading] = useState(true);

  // New State for View Modes
  const [viewMode, setViewMode] = useState('split'); // 'split' | 'overlay'
  const [overlayOpacity, setOverlayOpacity] = useState(50); // 0-100

  useEffect(() => {
    if (!isVisible || !coordinates || !streetViewRef.current) return;

    // Check if Google Maps is loaded
    if (!window.google || !window.google.maps) {
      setStreetViewError('Google Maps not loaded. Please add your API key.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setStreetViewError(null);

    // Check Street View availability first
    const streetViewService = new window.google.maps.StreetViewService();

    streetViewService.getPanorama(
      {
        location: { lat: coordinates.lat, lng: coordinates.lon },
        radius: 100, // Search within 100 meters
        preference: window.google.maps.StreetViewPreference.NEAREST
      },
      (data, status) => {
        if (status === window.google.maps.StreetViewStatus.OK) {
          // Street View is available
          panorama.current = new window.google.maps.StreetViewPanorama(
            streetViewRef.current,
            {
              position: { lat: coordinates.lat, lng: coordinates.lon },
              pov: { heading: 0, pitch: 0 },
              zoom: 1,
              addressControl: false,
              showRoadLabels: true,
              motionTracking: false,
              motionTrackingControl: false,
              fullscreenControl: false,
              zoomControl: true,
              zoomControlOptions: {
                position: window.google.maps.ControlPosition.RIGHT_CENTER
              }
            }
          );
          setLoading(false);
        } else {
          setStreetViewError('Street View not available at this location');
          setLoading(false);
        }
      }
    );

    return () => {
      if (panorama.current) {
        // Clean up panorama
        panorama.current = null;
      }
    };
  }, [coordinates, isVisible]);

  if (!isVisible) return null;

  return (
    <div className="absolute inset-0 z-50 flex bg-[#0a0a0a]">
      {/* Background (Street View) - Always full width in overlay mode, flex-1 in split */}
      <div className={`relative h-full transition-all duration-500 ease-in-out ${viewMode === 'split' ? 'w-1/2 border-r border-white/10' : 'w-full'}`}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#1a1a1a] z-10">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-gray-400 font-mono text-sm">INITIALIZING SATELLITE LINK...</p>
            </div>
          </div>
        )}

        {streetViewError && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#1a1a1a] z-10">
            <div className="text-center max-w-sm">
              <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
              <p className="text-white font-medium mb-2">Street View Unavailable</p>
              <p className="text-gray-400 text-sm">{streetViewError}</p>
              <button
                onClick={onBack}
                className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Return to Map
              </button>
            </div>
          </div>
        )}

        <div
          ref={streetViewRef}
          className="w-full h-full"
          style={{ display: streetViewError ? 'none' : 'block' }}
        />

        {/* Street View Label */}
        <div className="absolute top-4 left-24 px-3 py-1.5 bg-black/60 backdrop-blur-md border border-white/10 rounded-lg flex items-center gap-2 pointer-events-none">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          <span className="text-xs text-white/90 font-medium">LIVE STREET VIEW</span>
        </div>
      </div>

      {/* Foreground (Uploaded Image) */}
      <div
        className={`relative transition-all duration-500 ease-in-out bg-[#111] overflow-hidden
           ${viewMode === 'split' ? 'w-1/2' : 'absolute inset-0 pointer-events-none'}
         `}
        style={{ opacity: viewMode === 'split' ? 1 : overlayOpacity / 100 }}
      >
        <img
          src={uploadedImage}
          alt="Uploaded"
          className="w-full h-full object-contain"
        />

        {/* Crosshair Overlay - Only visible in Split Mode to avoid cluttering Overlay Mode */}
        {viewMode === 'split' && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-50">
            <div className="relative">
              <div className="w-16 h-16 border border-white/30 rounded-full" />
              <div className="absolute top-1/2 left-1/2 w-4 h-[1px] bg-white/30 -translate-x-1/2 -translate-y-1/2" />
              <div className="absolute top-1/2 left-1/2 w-[1px] h-4 bg-white/30 -translate-x-1/2 -translate-y-1/2" />
            </div>
          </div>
        )}

        {/* Image Label */}
        <div className={`absolute top-4 px-3 py-1.5 bg-black/60 backdrop-blur-md border border-white/10 rounded-lg pointer-events-none transition-all duration-300 ${viewMode === 'split' ? 'right-4' : 'right-4 opacity-50'}`}>
          <span className="text-xs text-white/90 font-medium">SOURCE IMAGE</span>
        </div>
      </div>

      {/* Floating Controls Overlay */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-4 w-full max-w-md px-4 pointer-events-none">

        {/* Opacity Slider (Only for Overlay Mode) */}
        <div className={`transition-all duration-300 w-full ${viewMode === 'overlay' ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
          <div className="bg-black/80 backdrop-blur-xl border border-white/10 rounded-xl p-4 shadow-2xl">
            <div className="flex justify-between text-xs text-gray-400 mb-2 font-medium uppercase tracking-wider">
              <span>Street View</span>
              <span>Overlay Opacity: {overlayOpacity}%</span>
              <span>Source</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={overlayOpacity}
              onChange={(e) => setOverlayOpacity(e.target.value)}
              className="w-full accent-primary-500 h-1.5 bg-white/20 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>

        {/* Mode Toggle Bar */}
        <div className="bg-black/80 backdrop-blur-xl border border-white/10 rounded-full p-1.5 flex items-center gap-1 shadow-2xl pointer-events-auto">
          <button
            onClick={() => setViewMode('split')}
            className={`px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-2 transition-all ${viewMode === 'split' ? 'bg-white text-black shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
          >
            <div className="flex gap-0.5">
              <div className="w-2 h-3 border border-current rounded-[1px]" />
              <div className="w-2 h-3 border border-current rounded-[1px]" />
            </div>
            Split View
          </button>
          <button
            onClick={() => setViewMode('overlay')}
            className={`px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-2 transition-all ${viewMode === 'overlay' ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
          >
            <div className="relative w-4 h-3 flex items-center justify-center">
              <div className="absolute inset-0 border border-current rounded-[1px] opacity-100" />
              <div className="absolute inset-0 border border-current rounded-[1px] opacity-50 bg-current scale-75" />
            </div>
            Detective Overlay
          </button>
        </div>
      </div>

      {/* Back button */}
      <button
        onClick={onBack}
        className="absolute top-4 left-4 flex items-center gap-2 px-4 py-2.5
                 bg-black/60 backdrop-blur-md text-white rounded-xl border border-white/10
                 hover:bg-white/10 transition-colors z-20 group"
      >
        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
        <span className="font-medium text-sm">Back to Analysis</span>
      </button>

      {/* Coordinates badge - now smaller and integrated */}
      {coordinates && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-md text-white text-xs px-3 py-1.5 rounded-full border border-white/10 z-20 font-mono text-gray-400 hidden md:block">
          {coordinates.lat?.toFixed(5)}, {coordinates.lon?.toFixed(5)}
        </div>
      )}
    </div>
  );
}
